import html2canvas from 'html2canvas';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { createRoot } from 'react-dom/client';
import React from 'react';
import { calculateMapBounds, formatCoordinates } from './mapUtils';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in Leaflet with React
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface MapImageGeneratorProps {
  latitude: number;
  longitude: number;
  area?: number | null;
}

const MapComponent: React.FC<MapImageGeneratorProps & { onMapReady: () => void }> = ({ 
  latitude, 
  longitude, 
  area,
  onMapReady 
}) => {
  const bounds = calculateMapBounds(latitude, longitude, area);
  const coordinatesText = formatCoordinates(latitude, longitude);

  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      style={{ height: '400px', width: '600px' }}
      scrollWheelZoom={false}
      {...({whenReady: (map: any) => {
        map.target.fitBounds(bounds, { padding: [20, 20] });
        
        // Wait for tiles to load by checking the DOM
        const checkTiles = () => {
          const container = map.target.getContainer();
          const tiles = container.querySelectorAll('img.leaflet-tile');
          const loadedTiles = container.querySelectorAll('img.leaflet-tile.leaflet-tile-loaded');
          
          // Check if we have a reasonable number of loaded tiles
          if (loadedTiles.length >= Math.min(4, tiles.length) || tiles.length === 0) {
            // Wait a bit more for rendering
            setTimeout(() => {
              onMapReady();
            }, 1000);
          } else {
            // Check again
            setTimeout(checkTiles, 200);
          }
        };
        
        // Start checking after a short delay
        setTimeout(checkTiles, 500);
      }} as any)}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.esri.com/">Esri</a> &copy; <a href="https://www.google.com/maps">Google</a>'
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
      />
      <Marker position={[latitude, longitude]}>
        <Popup>
          {coordinatesText}
        </Popup>
      </Marker>
    </MapContainer>
  );
};

/**
 * Generate a map image data URL for PDF rendering
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @param area Area in hectares (optional)
 * @returns Promise<string> Data URL of the map image
 */
export const generateMapImage = async (
  latitude: number,
  longitude: number,
  area?: number | null
): Promise<string | null> => {
  try {
    // Create a temporary container (off-screen)
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px'; // Move off-screen
    container.style.top = '0';
    container.style.width = '600px';
    container.style.height = '400px';
    container.style.zIndex = '-1';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'hidden';
    container.style.backgroundColor = '#ffffff';
    document.body.appendChild(container);

    // Create React root and render map
    const root = createRoot(container);
    
    // Wait for map to be ready and tiles to load
    await new Promise<void>((resolve) => {
      root.render(
        <MapComponent 
          latitude={latitude} 
          longitude={longitude} 
          area={area}
          onMapReady={resolve}
        />
      );
      
      // Fallback timeout in case tiles don't load
      setTimeout(() => {
        resolve();
      }, 15000);
    });

    // Move container on-screen and make it visible just for capture
    container.style.left = '0';
    container.style.opacity = '1';
    
    // Additional delay to ensure all rendering is complete
    await new Promise(resolve => setTimeout(resolve, 500));

    // Capture the map as an image
    const canvas = await html2canvas(container, {
      width: 600,
      height: 400,
      useCORS: true,
      allowTaint: false, // Set to false to avoid tainted canvas issues
      logging: false,
      backgroundColor: '#ffffff',
      scale: 1,
      removeContainer: false,
      imageTimeout: 15000,
    });

    // Immediately hide it again after capture
    container.style.opacity = '0';
    container.style.left = '-9999px';

    // Clean up
    root.unmount();
    document.body.removeChild(container);

    // Return data URL
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error generating map image:', error);
    return null;
  }
};

